import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { syncPlaqueMessageNote } from '@theme/tc-message-note-sync';
import '@theme/dialog';

const messageProperty = 'Mesaj pe placuta din lemn';
const requestProperty = '_tc_landing_request';
const jsonUrl = (url) => url.replace(/\.js$/, '') + '.js';

function cartFailure(kind) {
  const error = new Error(kind);
  error.kind = kind;
  return error;
}

export async function addGiftToCart(order, dependencies = {}) {
  if (!/^\d+$/.test(String(order.variantId)) || !Number.isInteger(order.quantity) || order.quantity < 1) {
    throw cartFailure('invalid');
  }
  const message = typeof order.message === 'string' ? order.message.trim() : '';
  const properties = { [requestProperty]: order.requestId };
  if (message) properties[messageProperty] = message;
  const request = dependencies.request || ((url, options) => fetch(url, options));
  const syncNote = dependencies.syncNote || syncPlaqueMessageNote;
  const readCart = async () => {
    const response = await request(jsonUrl(order.cartUrl), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(10000) });
    if (!response.ok) throw cartFailure('uncertain');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw cartFailure('uncertain');
    return cart;
  };
  let added;
  let cart;
  try {
    const response = await request(jsonUrl(order.addUrl), {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, signal: AbortSignal.timeout(20000),
      body: JSON.stringify({ items: [{ id: Number(order.variantId), quantity: order.quantity, properties }], sections: order.sections, sections_url: order.sectionsUrl })
    });
    if (!response.ok && response.status >= 400 && response.status < 500) throw cartFailure('rejected');
    if (!response.ok) throw cartFailure('uncertain');
    added = await response.json();
    if (added.status) throw cartFailure('rejected');
    const items = added.items || [added];
    if (!items.some((item) => String(item.variant_id || item.id) === String(order.variantId) && item.properties?.[requestProperty] === order.requestId)) throw cartFailure('uncertain');
  } catch (error) {
    if (error.kind === 'rejected') throw error;
    try { cart = await readCart(); } catch { throw cartFailure('uncertain'); }
    if (!cart.items.some((item) => String(item.variant_id || item.id) === String(order.variantId) && item.properties?.[requestProperty] === order.requestId)) throw cartFailure('uncertain');
  }
  let noteSynced = true;
  try { cart = await syncNote({ added: true, sections: order.sections.join(',') }); } catch { noteSynced = false; }
  if (!cart?.items) {
    try { cart = await readCart(); } catch { cart = null; }
  }
  return { cart, sections: cart?.sections || added?.sections, noteSynced };
}

class TCGiftLanding extends HTMLElement {
  #controller;
  #busy = false;
  #uncertain = false;
  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.form = this.querySelector('[data-tc-order-form]');
    this.status = this.querySelector('[data-tc-status]');
    this.message = this.form?.querySelector('textarea');
    this.quantity = this.form?.querySelector('[name="quantity"]');
    if (this.form) {
      this.form.noValidate = true;
      this.form.addEventListener('submit', (event) => this.#submit(event), { signal });
      this.message.addEventListener('input', () => this.#messageChanged(), { signal });
      this.quantity.addEventListener('input', () => this.quantity.setCustomValidity(''), { signal });
      window.addEventListener('pageshow', (event) => { if (event.persisted && !this.#busy) this.#resetForm(); }, { signal });
    }
    this.addEventListener('click', (event) => {
      const target = event.target.closest('a,button');
      if (!target) return;
      if (target.matches('[data-tc-open-cart]')) { event.preventDefault(); this.#openCart(); }
      if (target.matches('[data-tc-clear-message]')) { this.message.value = ''; this.#messageChanged(); this.message.focus(); }
      if (target.matches('[data-tc-edit-message]') && this.message) { event.preventDefault(); this.message.focus(); }
    }, { signal });
    document.addEventListener(CartAddEvent.eventName, (event) => {
      const count = event.detail?.resource?.item_count;
      if (Number.isInteger(count)) this.querySelectorAll('[data-tc-cart-count]').forEach((node) => { node.textContent = count ? ` (${count})` : ''; });
    }, { signal });
  }
  disconnectedCallback() { this.#controller?.abort(); }
  #messageChanged() {
    this.querySelector('[data-tc-clear-message]').hidden = !this.message.value;
    if (this.status?.classList.contains('is-error') && !this.#uncertain) this.#setStatus('');
  }
  #setStatus(text, error = false) {
    if (!this.status) return;
    this.status.textContent = text;
    this.status.classList.toggle('is-error', error);
  }
  #setBusy(value) {
    this.#busy = value;
    this.setAttribute('aria-busy', String(value));
    this.querySelectorAll('[data-tc-add]').forEach((button) => {
      button.disabled = value || this.#uncertain || this.dataset.available !== 'true';
      const label = button.querySelector('[data-tc-button-label]');
      if (!button.dataset.restLabel) button.dataset.restLabel = label.textContent;
      label.textContent = value ? this.dataset.addingLabel : button.dataset.restLabel;
    });
    if (this.quantity) this.quantity.disabled = value || this.dataset.available !== 'true';
    if (this.message) this.message.disabled = value || this.dataset.available !== 'true';
  }
  #resetForm() {
    this.form.reset();
    this.message.value = '';
    this.#messageChanged();
  }
  #openCart() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') { if (!drawer.querySelector('dialog')?.open) drawer.open(); }
    else window.location.assign(this.dataset.cartUrl);
  }
  async #submit(event) {
    event.preventDefault();
    if (this.#busy || this.#uncertain || this.dataset.available !== 'true') return;
    if (!this.form.reportValidity()) return;
    const data = new FormData(this.form);
    if (String(data.get('id')) !== this.dataset.variantId) { this.#setStatus(this.dataset.errorLabel, true); return; }
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((node) => node.dataset.sectionId))].slice(0, 5);
    const order = { variantId: this.dataset.variantId, quantity: Number(data.get('quantity')), message: this.message.value.trim(), requestId: crypto.randomUUID(), sections, sectionsUrl: window.location.pathname, addUrl: this.form.action, cartUrl: this.dataset.cartUrl };
    this.#setStatus('');
    this.#setBusy(true);
    try {
      const result = await addGiftToCart(order);
      this.#resetForm();
      this.#setStatus(this.dataset.successLabel);
      this.dispatchEvent(new CartAddEvent(result.cart || undefined, order.variantId, { source: 'product-form-component', origin: 'tc-gift-landing', itemCount: order.quantity, productId: this.dataset.productId, sections: result.sections }));
      this.#openCart();
    } catch (error) {
      this.#uncertain = error.kind === 'uncertain';
      this.#setStatus(this.#uncertain ? this.dataset.uncertainLabel : this.dataset.errorLabel, true);
      this.querySelector('[data-tc-recovery]').hidden = !this.#uncertain;
      this.dispatchEvent(new CartErrorEvent(this.form.id, this.dataset.errorLabel, '', {}));
    } finally { this.#setBusy(false); }
  }
}

if (!customElements.get('tc-gift-landing')) customElements.define('tc-gift-landing', TCGiftLanding);

class TCGiftGallery extends HTMLElement {
  #controller;
  #resizeObserver;
  #index = 0;
  #zoom = 1;
  #frame;
  #drag;
  #suppressClick = false;
  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbnails = [...this.querySelectorAll('[data-gallery-thumbnail]')];
    this.dialogComponent = this.querySelector('dialog-component');
    this.dialog = this.querySelector('dialog');
    this.zoomViewport = this.querySelector('[data-gallery-zoom-viewport]');
    this.zoomCanvas = this.querySelector('[data-gallery-zoom-canvas]');
    this.zoomImage = this.querySelector('[data-gallery-zoom-image]');
    if (!this.track || !this.slides.length) return;
    this.addEventListener('click', (event) => this.#click(event), { signal });
    this.addEventListener('keydown', (event) => this.#key(event), { signal });
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#select(Math.round(this.track.scrollLeft / this.track.clientWidth)));
    }, { passive: true, signal });
    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.#drag = { x: event.clientX, scroll: this.track.scrollLeft, pointer: event.pointerId, moved: false };
    }, { signal });
    this.track.addEventListener('pointermove', (event) => {
      if (!this.#drag || event.pointerId !== this.#drag.pointer) return;
      const distance = event.clientX - this.#drag.x;
      if (!this.#drag.moved && Math.abs(distance) < 8) return;
      this.#drag.moved = true;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
      this.track.scrollLeft = this.#drag.scroll - distance;
    }, { signal });
    for (const type of ['pointerup', 'pointercancel']) this.track.addEventListener(type, () => this.#endDrag(), { signal });
    this.dialogComponent.addEventListener('dialog:close', () => {
      this.#setZoom(1);
      this.slides[this.#index].querySelector('[data-gallery-open]').focus({ preventScroll: true });
    }, { signal });
    this.zoomViewport.addEventListener('dblclick', () => this.#setZoom(this.#zoom > 1 ? 1 : 2), { signal });
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.track.scrollTo({ left: this.#index * this.track.clientWidth, behavior: 'instant' }));
    this.#resizeObserver.observe(this.track);
    this.#select(0);
  }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }
  #endDrag() {
    if (!this.#drag) return;
    const moved = this.#drag.moved;
    const pointer = this.#drag.pointer;
    this.#drag = null;
    this.track.classList.remove('is-dragging');
    if (this.track.hasPointerCapture(pointer)) this.track.releasePointerCapture(pointer);
    if (moved) {
      this.#suppressClick = true;
      requestAnimationFrame(() => { this.#suppressClick = false; });
      this.#go(Math.round(this.track.scrollLeft / this.track.clientWidth));
    }
  }
  #hydrate(index) {
    const image = this.slides[index]?.querySelector('img');
    if (!image?.dataset.gallerySrc) return;
    image.sizes = image.dataset.gallerySizes;
    image.srcset = image.dataset.gallerySrcset;
    image.src = image.dataset.gallerySrc;
    delete image.dataset.gallerySrc;
  }
  #select(index) {
    this.#index = Math.max(0, Math.min(this.slides.length - 1, index));
    this.#hydrate(this.#index);
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', String(i !== this.#index));
      slide.querySelector('[data-gallery-open]').tabIndex = i === this.#index ? 0 : -1;
    });
    this.thumbnails.forEach((button, i) => button.setAttribute('aria-pressed', String(i === this.#index)));
    this.querySelectorAll('[data-gallery-counter]').forEach((node) => { node.textContent = `${this.#index + 1} / ${this.slides.length}`; });
    this.querySelectorAll('[data-gallery-caption]').forEach((node) => { node.textContent = this.slides[this.#index].dataset.caption; });
    this.querySelectorAll('[data-gallery-prev]').forEach((button) => { button.disabled = this.#index === 0; });
    this.querySelectorAll('[data-gallery-next]').forEach((button) => { button.disabled = this.#index === this.slides.length - 1; });
    const selected = this.thumbnails[this.#index];
    const strip = selected.parentElement;
    strip.scrollTo({ left: Math.max(0, selected.offsetLeft - strip.offsetLeft - (strip.clientWidth - selected.clientWidth) / 2), behavior: 'instant' });
    if (this.dialog.open) this.#updateZoomImage();
  }
  #go(index) {
    this.#select(index);
    this.track.scrollTo({ left: this.#index * this.track.clientWidth, behavior: 'instant' });
  }
  #updateZoomImage() {
    const slide = this.slides[this.#index];
    if (this.zoomImage.getAttribute('src') !== slide.dataset.large) {
      this.zoomImage.src = slide.dataset.large;
      this.zoomImage.alt = slide.querySelector('img').alt;
      this.#setZoom(1);
    }
  }
  #setZoom(value) {
    this.#zoom = Math.max(1, Math.min(3, value));
    this.zoomCanvas.style.setProperty('--tc-gallery-zoom', this.#zoom);
    this.querySelector('[data-gallery-zoom-out]').disabled = this.#zoom === 1;
    this.querySelector('[data-gallery-zoom-in]').disabled = this.#zoom === 3;
    requestAnimationFrame(() => this.zoomViewport.scrollTo({ left: (this.zoomViewport.scrollWidth - this.zoomViewport.clientWidth) / 2, top: (this.zoomViewport.scrollHeight - this.zoomViewport.clientHeight) / 2, behavior: 'instant' }));
  }
  #open() {
    this.#updateZoomImage();
    this.dialogComponent.showDialog();
  }
  #click(event) {
    const target = event.target.closest('a,button');
    if (!target) return;
    if (target.matches('[data-gallery-open]')) {
      event.preventDefault();
      if (this.#suppressClick) { this.#suppressClick = false; return; }
      this.#go(this.slides.indexOf(target.closest('[data-gallery-slide]')));
      this.#open();
    }
    if (target.matches('[data-gallery-enlarge]')) this.#open();
    if (target.matches('[data-gallery-thumbnail]')) this.#go(Number(target.dataset.galleryThumbnail));
    if (target.matches('[data-gallery-prev]')) this.#go(this.#index - 1);
    if (target.matches('[data-gallery-next]')) this.#go(this.#index + 1);
    if (target.matches('[data-gallery-close]')) this.dialogComponent.closeDialog();
    if (target.matches('[data-gallery-zoom-in]')) this.#setZoom(this.#zoom + .5);
    if (target.matches('[data-gallery-zoom-out]')) this.#setZoom(this.#zoom - .5);
    if (target.matches('[data-gallery-fit]')) this.#setZoom(1);
  }
  #key(event) {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === 'Escape') return;
    const inGallery = this.dialog.open || this.track.contains(event.target) || event.target.matches('[data-gallery-thumbnail]');
    if (!inGallery) return;
    const destination = { ArrowLeft: this.#index - 1, ArrowRight: this.#index + 1, Home: 0, End: this.slides.length - 1 }[event.key];
    if (destination !== undefined) { event.preventDefault(); this.#go(destination); }
    if (this.dialog.open && ['+', '=', '-', '0'].includes(event.key)) {
      event.preventDefault();
      this.#setZoom(event.key === '0' ? 1 : this.#zoom + (event.key === '-' ? -.5 : .5));
    }
  }
}

if (!customElements.get('tc-gift-gallery')) customElements.define('tc-gift-gallery', TCGiftGallery);
